'use client';

import * as SubframeCore from '@subframe/core';
import { FeatherCheck, FeatherChevronsUpDown, FeatherPlus, FeatherSettings } from '@subframe/core';
import { useRouter } from 'next/navigation';
import React from 'react';

import { Avatar } from '@/shared/components/ui/common/Avatar';
import type { Escritorio } from '@/types/escritorio/escritorioCrudTypes';

interface WorkspaceSwitcherProps {
  selected: Escritorio | null;
  items: Escritorio[];
  loading: boolean;
  total: number;
  query: string;
  favorites: string[];
  onQueryChange: (query: string) => void;
  onToggleFavorite: (id: string) => void;
  onSelectWorkspace: (workspace: Escritorio) => void;
  onLoadMore: () => void;
  onCreateClick: () => void;
  className?: string;
  isCollapsed?: boolean;
}

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  selected,
  items,
  loading,
  total,
  query,
  favorites,
  onQueryChange,
  onToggleFavorite,
  onSelectWorkspace,
  onLoadMore,
  onCreateClick,
  className,
  isCollapsed = false,
}) => {
  const router = useRouter();

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getWorkspaceTag = (tipo: string | undefined) => {
    if (!tipo) return null;
    return tipo === 'PERSONAL' ? 'Pessoal' : 'Organização';
  };

  const getTagClasses = (tipo: string | undefined) => {
    if (tipo === 'PERSONAL') {
      return 'rounded bg-success-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-success-700';
    }
    return 'rounded bg-information-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-information-700';
  };

  return (
    <div className={`w-full ${className || ''}`.trim()}>
      <SubframeCore.DropdownMenu.Root>
        <SubframeCore.DropdownMenu.Trigger asChild={true}>
          <button
            type="button"
            data-testid="workspace-switcher-trigger"
            className={`group flex w-full items-center gap-3 rounded-md border border-solid transition-all focus-visible:ring-0 focus-visible:outline-none ${
              isCollapsed
                ? 'justify-center border-transparent p-2 hover:bg-neutral-100'
                : 'bg-default-background border-neutral-200 px-3 py-2.5 hover:bg-neutral-50'
            }`}
          >
            <Avatar size="small" square={true} variant="brand">
              {loading ? '...' : selected?.nomeFantasia ? getInitials(selected.nomeFantasia) : 'EP'}
            </Avatar>
            {!isCollapsed && (
              <>
                <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                  <div className="flex w-full items-center gap-2">
                    <span
                      data-testid="workspace-switcher-active"
                      className="text-caption-bold font-caption-bold text-default-font min-w-0 flex-1 truncate text-left"
                    >
                      {selected?.nomeFantasia || 'Espaço pessoal'}
                    </span>
                    {selected?.guestAccess && (
                      <span className="bg-warning-50 text-warning-700 rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase">
                        Convidado
                      </span>
                    )}
                  </div>
                  {selected?.tipo && (
                    <span className="text-subtext-color text-[10px] font-medium tracking-wide uppercase">
                      {getWorkspaceTag(selected.tipo)}
                    </span>
                  )}
                </div>
                <FeatherChevronsUpDown className="h-4 w-4 flex-none text-neutral-400" />
              </>
            )}
          </button>
        </SubframeCore.DropdownMenu.Trigger>
        <SubframeCore.DropdownMenu.Portal>
          <SubframeCore.DropdownMenu.Content
            side="bottom"
            align="start"
            sideOffset={4}
            asChild={true}
          >
            <div
              data-testid="workspace-switcher-menu"
              className="z-[1000] flex w-72 flex-col items-start rounded-md border border-solid border-neutral-200 bg-white shadow-lg"
            >
              <div className="flex w-full flex-col items-start gap-3 border-b border-solid border-neutral-100 px-4 py-3">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Alternar espaço de trabalho
                </span>
                <input
                  type="text"
                  data-testid="workspace-switcher-search"
                  value={query}
                  onChange={(e) => onQueryChange(e.target.value)}
                  className="text-body font-body text-default-font focus:border-brand-600 focus:ring-brand-600/10 w-full rounded-md border border-solid border-neutral-200 bg-white px-3 py-2 outline-none placeholder:text-neutral-400 focus:ring-2"
                  placeholder="Buscar espaço de trabalho..."
                />
              </div>

              <div className="flex max-h-80 w-full flex-col items-start overflow-y-auto">
                {items.map((item) => {
                  const isSelected = item.id === selected?.id;

                  return (
                    <SubframeCore.DropdownMenu.Item
                      key={item.id}
                      asChild={true}
                      onSelect={() => onSelectWorkspace(item)}
                    >
                      <div
                        data-testid={`workspace-switcher-item-${item.id}`}
                        className={`flex w-full items-center gap-3 border-l-4 px-4 py-2.5 transition-colors ${
                          isSelected
                            ? 'bg-brand-50 border-brand-600'
                            : 'border-transparent hover:bg-neutral-50'
                        }`}
                      >
                        <Avatar
                          size="small"
                          square={true}
                          variant={isSelected ? 'brand' : 'neutral'}
                        >
                          {getInitials(item.nomeFantasia)}
                        </Avatar>
                        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                          <div className="flex w-full items-center gap-2">
                            <span className="text-body-bold font-body-bold text-default-font truncate">
                              {item.nomeFantasia}
                            </span>
                            {!item.ativo && (
                              <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600">
                                INATIVO
                              </span>
                            )}
                            {item.guestAccess && (
                              <span className="bg-warning-50 text-warning-700 rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase">
                                Convidado
                              </span>
                            )}
                          </div>
                          {item.tipo && (
                            <span className={getTagClasses(item.tipo)}>
                              {getWorkspaceTag(item.tipo)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isSelected && (
                            <FeatherCheck className="text-brand-600 h-4 w-4 flex-none" />
                          )}
                        </div>
                      </div>
                    </SubframeCore.DropdownMenu.Item>
                  );
                })}

                {loading && (
                  <div className="w-full px-4 py-3">
                    <span className="text-body font-body text-subtext-color">Carregando...</span>
                  </div>
                )}

                {!loading && items.length < total && (
                  <button
                    type="button"
                    onClick={onLoadMore}
                    className="text-body font-body text-brand-primary w-full px-4 py-3 text-left hover:bg-neutral-50 focus-visible:ring-0 focus-visible:outline-none"
                  >
                    Carregar mais
                  </button>
                )}

                {!loading && items.length === 0 && (
                  <div className="w-full px-4 py-3">
                    <span className="text-body font-body text-subtext-color">
                      Nenhum resultado encontrado
                    </span>
                  </div>
                )}
              </div>

              <div className="w-full border-t border-solid border-neutral-100">
                <SubframeCore.DropdownMenu.Item asChild={true} onSelect={onCreateClick}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50 focus-visible:ring-0 focus-visible:outline-none"
                  >
                    <FeatherPlus className="text-subtext-color h-4 w-4" />
                    <span className="text-body font-body text-default-font">
                      Criar nova organização
                    </span>
                  </button>
                </SubframeCore.DropdownMenu.Item>

                <SubframeCore.DropdownMenu.Item
                  asChild={true}
                  onSelect={() => router.push('/configuracoes')}
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50 focus-visible:ring-0 focus-visible:outline-none"
                  >
                    <FeatherSettings className="text-subtext-color h-4 w-4" />
                    <span className="text-body font-body text-default-font">Gerenciar Espaços</span>
                  </button>
                </SubframeCore.DropdownMenu.Item>
              </div>
            </div>
          </SubframeCore.DropdownMenu.Content>
        </SubframeCore.DropdownMenu.Portal>
      </SubframeCore.DropdownMenu.Root>
    </div>
  );
};
